import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";

const gbp = (n) => `£${n.toFixed(2)}`;

export const BundleCard = ({ bundle, index = 0 }) => {
  const { addItem } = useCart();
  const savings = bundle.value - bundle.price;

  const add = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!addItem(bundle, "bundle", 1)) return;
    toast.success(`${bundle.name} added to cart`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
    >
      <Link to={`/bundle/${bundle.id}`} data-testid="bundle-card" className="group relative flex h-full flex-col overflow-hidden rounded-2xl bg-gradient-to-b from-amber-50/70 to-white ring-1 ring-amber-300/70 shadow-[0_20px_50px_-20px_rgba(217,169,66,0.5)] transition-all duration-500 hover:ring-amber-400 hover:shadow-[0_26px_70px_-18px_rgba(217,169,66,0.65)] hover:-translate-y-1">
        <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
          <img src={bundle.image} alt={bundle.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E]/90 via-[#0A0B0E]/20 to-transparent" />
          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 px-3 py-1 text-[10px] font-mono2 font-semibold uppercase tracking-widest text-amber-950 shadow-[0_2px_10px_rgba(217,169,66,0.6)]">
            <Sparkles className="h-3 w-3" /> Best Value
          </span>
          {savings > 0 && (
            <span className="absolute right-3 top-3 rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 px-3 py-1 text-xs font-mono2 font-semibold text-amber-950 shadow-[0_2px_10px_rgba(217,169,66,0.6)]">
              Save {gbp(savings)}
            </span>
          )}
          <div className="absolute bottom-4 left-5">
            <span className="font-mono2 text-[10px] uppercase tracking-widest text-amber-200">Bundle</span>
            <h3 className="font-display text-xl font-semibold text-white">{bundle.name}</h3>
          </div>
        </div>

        <div className="flex flex-1 flex-col p-5">
          <p className="text-sm leading-relaxed text-slate-600">{bundle.tagline}</p>
          <ul className="mt-4 space-y-2">
            {bundle.includes.map((it) => (
              <li key={it} className="flex items-center gap-2 text-sm text-slate-700">
                <Check className="h-4 w-4 shrink-0 text-amber-500" /> {it}
              </li>
            ))}
          </ul>
          <div className="mt-5 flex items-end justify-between border-t border-amber-200/70 pt-4">
            <div>
              {savings > 0 && <span className="text-sm text-slate-400 line-through">{gbp(bundle.value)}</span>}
              <p className="text-2xl font-bold bg-gradient-to-r from-amber-500 to-yellow-600 bg-clip-text text-transparent">{gbp(bundle.price)}</p>
            </div>
            <Button onClick={add} disabled={bundle.sold_out} data-testid={`bundle-add-${bundle.id}`} className="disabled:opacity-60 bg-gradient-to-r from-amber-400 to-yellow-500 font-semibold text-amber-950 hover:from-amber-300 hover:to-yellow-400 shadow-[0_4px_14px_rgba(217,169,66,0.45)]">
              {bundle.sold_out ? "Sold out" : <><Plus className="mr-1.5 h-4 w-4" /> Add</>}
            </Button>
          </div>
          <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-amber-700 group-hover:text-amber-800">
            View bundle <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

export default BundleCard;
